import React, { useState } from 'react'
import {
  Box, Button, Dialog, DialogContent, DialogTitle, IconButton,
  Typography, makeStyles, useMediaQuery, useTheme
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import { toFarsiDigits, thousandsSeparate } from '../../assets/utils'

const useStyles = makeStyles((theme) => ({
  dialog: { direction: 'rtl' },
  title: {
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    color: '#fff',
    padding: theme.spacing(2, 3),
  },
  closeBtn: { color: '#fff', position: 'absolute', left: 8, top: 8 },
  content: { padding: theme.spacing(3) },
  amountBox: {
    background: '#f3f4f6',
    borderRadius: 12,
    padding: theme.spacing(2),
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  amountText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#c62828',
    letterSpacing: 2,
  },
  cardBox: {
    background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 100%)',
    borderRadius: 14,
    padding: theme.spacing(2.5, 3),
    color: '#fff',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: theme.spacing(2),
    },
  },
  cardNumber: {
    fontSize: 22,
    fontWeight: 'bold',
    letterSpacing: 6,
    direction: 'ltr',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    [theme.breakpoints.down('xs')]: {
      fontSize: 18,
      letterSpacing: 3,
    },
  },
  copyBtn: {
    color: '#fff',
    [theme.breakpoints.down('xs')]: {
      alignSelf: 'flex-end',
      marginTop: theme.spacing(-1),
    },
  },
  stepsBox: {
    background: '#fff8e1',
    borderRadius: 10,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px solid #ffe082',
  },
  stepRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: theme.spacing(1),
  },
  stepNum: {
    minWidth: 26,
    height: 26,
    borderRadius: '50%',
    background: '#1a237e',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 'bold',
    flexShrink: 0,
    marginTop: 2,
  },
  phonesBox: {
    background: '#e8f5e9',
    borderRadius: 10,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  phoneNum: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2e7d32',
    direction: 'ltr',
    display: 'block',
    margin: theme.spacing(0.5, 0),
    letterSpacing: 2,
  },
  divider: { margin: theme.spacing(2, 0) },
  noteText: {
    color: '#666',
    fontSize: 13,
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },
}))

/**
 * راهنمای «پرداخت کارت به کارت» — فقط وقتی پرداختِ آنلاین از درگاه شکست خورده
 * به کاربر نشان داده می‌شود (به‌عنوان راهِ جایگزین).
 *
 * props:
 *   open/onClose : کنترلِ دیالوگ
 *   cardNumber   : شمارهٔ کارت (از تنظیمات سایت)
 *   phones       : آرایهٔ شماره‌های پشتیبانی
 *   amount       : مبلغِ قابل پرداخت (ریال) — اگر داده نشود، فقط راهنما نشان داده می‌شود
 *   years        : ۱ یا ۲ (برای متنِ «دورهٔ یک‌ساله/دوساله»)
 */
export default function CardToCardDialog({
  open,
  onClose,
  cardNumber,
  phones = [],
  amount = null,
  years = 1,
}) {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const [copied, setCopied] = useState(false)

  const handleCopyCard = () => {
    if (!cardNumber) return
    navigator.clipboard.writeText(cardNumber).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      className={classes.dialog}
    >
      <DialogTitle className={classes.title} disableTypography>
        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
          راهنمای پرداخت کارت به کارت
        </Typography>
        <IconButton className={classes.closeBtn} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {amount ? (
          <Box className={classes.amountBox}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              مبلغ قابل پرداخت (دورهٔ {years === 2 ? 'دوساله' : 'یکساله'})
            </Typography>
            <Typography className={classes.amountText}>
              {toFarsiDigits(thousandsSeparate(amount))} ریال
            </Typography>
          </Box>
        ) : null}

        <Box className={classes.cardBox}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" style={{ opacity: 0.8 }}>شماره کارت جهت واریز</Typography>
            <Typography className={classes.cardNumber}>{cardNumber || '—'}</Typography>
          </Box>
          <IconButton className={classes.copyBtn} onClick={handleCopyCard} title="کپی شماره کارت">
            <FileCopyOutlinedIcon />
          </IconButton>
        </Box>
        <Typography style={{
          textAlign: 'center', color: '#fff', fontSize: 14, fontWeight: 'bold',
          marginTop: -12, marginBottom: 10,
          background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 100%)',
          padding: '6px 16px', borderRadius: '0 0 14px 14px'
        }}>
          به نام: عبدالرحیم نمازی
        </Typography>
        {copied && (
          <Typography style={{ textAlign: 'center', color: 'green', marginBottom: 8, fontSize: 13 }}>
            ✓ شماره کارت کپی شد
          </Typography>
        )}

        <Box className={classes.divider} />

        <Box className={classes.stepsBox}>
          <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 10, textAlign: 'right' }}>
            مراحل فعال‌سازی اشتراک:
          </Typography>
            {[
              <>مبلغ <strong>{toFarsiDigits(thousandsSeparate(amount || 0))} ریال</strong> را به شماره کارت بالا واریز کنید</>,
              <>پس از واریز، <strong>شناسه ملی</strong> و <strong>نام شرکت</strong> خود را به یکی از شماره‌های زیر پیامک کنید</>,
              <>منتظر فعال‌سازی اشتراک خود باشید</>
            ].map((text, i) => (
              <Box key={i} className={classes.stepRow} style={{ flexDirection: 'row-reverse' }}>
                <Box className={classes.stepNum}>{toFarsiDigits(i + 1)}</Box>
                <Typography variant="body2" style={{ textAlign: 'right' }}>{text}</Typography>
              </Box>
            ))}
        </Box>

        {!!phones.length && (
          <Box className={classes.phonesBox}>
            <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
              شماره‌های پشتیبانی جهت ارسال اطلاعات:
            </Typography>
            {phones.map((p, i) => (
              <Typography key={i} className={classes.phoneNum}>{p}</Typography>
            ))}
          </Box>
        )}

        <Typography className={classes.noteText}>
          پس از تأیید پرداخت، اشتراک شما ظرف چند ساعت فعال شده و از طریق پیامک مطلع خواهید شد
        </Typography>
      </DialogContent>
    </Dialog>
  )
}
